/**
 * Definitive GL Account Type and Sub-Type Configuration
 *
 * SINGLE SOURCE OF TRUTH for account classification by GL code prefix.
 * Used for: account creation (prefix enforcement), COA display, Trial Balance grouping.
 * Does NOT affect backend logic - backend infers sub_type from code ranges independently.
 *
 * IMPORTANT: These labels MUST match the actual GL account names stored in the database.
 * The 2-digit prefix determines which sub-category an account belongs to.
 */

export const ACCOUNT_TYPE_CONFIG = {
  asset: {
    label: 'Assets',
    color: 'bg-blue-100 text-blue-700',
    borderColor: 'border-blue-200',
    subTypes: [
      { prefix: '10', label: 'Cash & Cash Equivalents', description: 'Cash on hand, bank accounts, petty cash' },
      { prefix: '11', label: 'Receivables', description: 'Accounts receivable, notes receivable' },
      { prefix: '12', label: 'Inventory & Prepaid', description: 'Merchandise, raw materials, prepaid expenses' },
      { prefix: '13', label: 'Other Short-term Assets', description: 'Advances, deposits, other current assets' },
      { prefix: '14', label: 'Tax Assets', description: 'VAT input, WHT recoverable, deferred tax assets' },
      { prefix: '15', label: 'Property, Plant & Equipment', description: 'Land, buildings, machinery, vehicles' },
      { prefix: '16', label: 'Right-of-Use Assets', description: 'Leased assets under IFRS 16' },
      { prefix: '17', label: 'Intangible Assets', description: 'Goodwill, patents, trademarks, software' },
      { prefix: '18', label: 'Investments', description: 'Fixed deposits, equity investments, bonds' },
      { prefix: '19', label: 'Other Non-Current Assets', description: 'Long-term deposits, deferred assets' },
    ]
  },
  liability: {
    label: 'Liabilities',
    color: 'bg-red-100 text-red-700',
    borderColor: 'border-red-200',
    subTypes: [
      { prefix: '20', label: 'Trade Payables & Accrued Liabilities', description: 'Accounts payable, accrued expenses, accrued salaries, due to directors/related parties' },
      { prefix: '21', label: 'Deferred Revenue & Customer Obligations', description: 'Unearned revenue, customer deposits, gift cards, contract liabilities' },
      { prefix: '22', label: 'Tax & Statutory Payables', description: 'VAT output, PAYE, corporate tax, pension, payroll taxes' },
      { prefix: '23', label: 'Other Tax Payables', description: 'Dividend tax, WHT corporate, additional statutory payables' },
      { prefix: '24', label: 'Short-term Borrowings', description: 'Short-term loans, credit cards, overdrafts, current portion of long-term debt' },
      { prefix: '25', label: 'Long-term Liabilities', description: 'Bank loans, mortgage, bonds payable, lease liabilities' },
      { prefix: '26', label: 'Other Non-Current Liabilities', description: 'Deferred taxes, pension obligations, provisions' },
      { prefix: '28', label: 'Deferred Revenue', description: 'Unearned revenue, deferred service/subscription/project revenue' },
    ]
  },
  equity: {
    label: 'Equity',
    color: 'bg-purple-100 text-purple-700',
    borderColor: 'border-purple-200',
    subTypes: [
      { prefix: '30', label: "Owner's Equity", description: "Owner's capital contributions" },
      { prefix: '31', label: 'Share Capital', description: 'Ordinary shares, preference shares, share premium' },
      { prefix: '32', label: 'Retained Earnings', description: 'Current year earnings, accumulated retained earnings' },
      { prefix: '33', label: 'Prior Year Earnings', description: 'Retained earnings from prior years' },
      { prefix: '34', label: 'Treasury Stock & Reserves', description: 'Treasury stock, capital reserves, statutory reserves' },
      { prefix: '35', label: 'Drawings', description: "Owner withdrawals, partner drawings" },
      { prefix: '36', label: 'Dividends', description: 'Dividends declared, dividends payable, interim dividends' },
      { prefix: '37', label: 'Currency & Other Reserves', description: 'Foreign currency translation reserve, other reserves' },
      { prefix: '38', label: 'Other Comprehensive Income', description: 'Fair value reserves, FX translation differences' },
      { prefix: '39', label: 'Opening Balance Equity', description: 'Opening balance equity for new accounting periods' },
    ]
  },
  revenue: {
    label: 'Revenue',
    color: 'bg-green-100 text-green-700',
    borderColor: 'border-green-200',
    subTypes: [
      { prefix: '40', label: 'Product Sales', description: 'Product sales, goods revenue, wholesale/retail' },
      { prefix: '41', label: 'Service Revenue', description: 'Service, consulting, subscription, training, platform fees' },
      { prefix: '42', label: 'Commission & Referral Income', description: 'Commission revenue, referral fees, affiliate income' },
      { prefix: '43', label: 'Rental & Licensing Income', description: 'Rental revenue, licensing fees' },
      { prefix: '44', label: 'Licensing Revenue', description: 'Software licensing, intellectual property licensing' },
      { prefix: '45', label: 'Investment Income', description: 'Dividends received, capital gains, fund distributions' },
      { prefix: '48', label: 'Other Operating Revenue', description: 'Unclassified revenue, returns & discounts' },
      { prefix: '49', label: 'Non-Operating & Miscellaneous Income', description: 'Interest income, gain on disposal, insurance claims, miscellaneous' },
    ]
  },
  expense: {
    label: 'Expenses',
    color: 'bg-orange-100 text-orange-700',
    borderColor: 'border-orange-200',
    subTypes: [
      { prefix: '50', label: 'Cost of Goods Sold', description: 'Direct costs, purchases, production costs' },
      { prefix: '51', label: 'COGS Adjustments', description: 'Inventory adjustments, write-offs, shrinkage' },
      { prefix: '52', label: 'Direct Costs - Digital', description: 'Data processing, server infrastructure, third-party API costs' },
      { prefix: '60', label: 'Salaries & Wages', description: 'Salaries, overtime, commission, bonuses, benefits, payroll taxes' },
      { prefix: '61', label: 'Other Personnel Costs', description: 'Staff welfare, temp staff, directors fees, payroll processing' },
      { prefix: '62', label: 'Technology & IT', description: 'Software, cloud hosting, cybersecurity, internet, IT support' },
      { prefix: '63', label: 'Occupancy & Facilities', description: 'Rent, utilities, property insurance, security, cleaning, repairs' },
      { prefix: '64', label: 'Professional Fees', description: 'Legal, accounting, audit, consulting, tax advisory, regulatory' },
      { prefix: '65', label: 'Marketing & Advertising', description: 'Advertising, digital marketing, trade shows, promotional materials' },
      { prefix: '66', label: 'Travel & Transport', description: 'Business travel, airfare, hotels, meals, vehicle costs, fuel' },
      { prefix: '67', label: 'Bank Charges & Financial Fees', description: 'Bank fees, payment processing, wire transfers, FX fees, interest' },
      { prefix: '68', label: 'Insurance, Depreciation & Amortization', description: 'Business insurance, professional indemnity, D&O, cyber, depreciation, amortization' },
      { prefix: '69', label: 'Other Operating Expenses & Losses', description: 'FX losses, bad debt, penalties, donations, R&D, shipping, miscellaneous' },
      { prefix: '70', label: 'Interest & Finance Costs', description: 'Loan interest, finance lease interest, overdraft interest' },
      { prefix: '71', label: 'Non-Operating Losses', description: 'Loss on disposal, impairment, write-offs, restructuring' },
    ]
  }
};

export const ACCOUNT_TYPES = Object.entries(ACCOUNT_TYPE_CONFIG).map(([value, config]) => ({
  value,
  label: config.label,
  color: config.color,
  borderColor: config.borderColor,
}));

/**
 * Get sub-type info for a given GL code based on its 2-digit prefix.
 */
export function getSubTypeForCode(code) {
  if (!code || code.length < 2) return null;
  const prefix = code.substring(0, 2);

  for (const [typeName, config] of Object.entries(ACCOUNT_TYPE_CONFIG)) {
    const subType = config.subTypes.find(st => st.prefix === prefix);
    if (subType) {
      return { type: typeName, typeLabel: config.label, ...subType };
    }
  }

  const fallbacks = {
    '1': { type: 'asset', typeLabel: 'Assets', label: 'Other Assets', prefix, description: '' },
    '2': { type: 'liability', typeLabel: 'Liabilities', label: 'Other Liabilities', prefix, description: '' },
    '3': { type: 'equity', typeLabel: 'Equity', label: 'Other Equity', prefix, description: '' },
    '4': { type: 'revenue', typeLabel: 'Revenue', label: 'Other Revenue', prefix, description: '' },
    '5': { type: 'expense', typeLabel: 'Expenses', label: 'Cost of Sales', prefix, description: '' },
    '6': { type: 'expense', typeLabel: 'Expenses', label: 'Operating Expenses', prefix, description: '' },
    '7': { type: 'expense', typeLabel: 'Expenses', label: 'Other Expenses', prefix, description: '' },
  };
  return fallbacks[code[0]] || { type: 'expense', typeLabel: 'Expenses', label: 'Uncategorized', prefix, description: '' };
}

/**
 * Group accounts by main type -> sub-type (2-digit prefix).
 * Returns { asset: { label, color, subGroups: [{ prefix, label, accounts }] }, ... }
 */
export function groupAccountsBySubType(accounts) {
  const result = {};
  for (const [type, config] of Object.entries(ACCOUNT_TYPE_CONFIG)) {
    result[type] = { label: config.label, color: config.color, borderColor: config.borderColor, subGroups: [], totalAccounts: 0 };
  }

  const prefixGroups = {};
  for (const acc of accounts) {
    const prefix = (acc.code || '').substring(0, 2);
    if (!prefixGroups[prefix]) prefixGroups[prefix] = [];
    prefixGroups[prefix].push(acc);
  }

  for (const [prefix, accs] of Object.entries(prefixGroups)) {
    const info = getSubTypeForCode(prefix + '00');
    if (!info || !result[info.type]) continue;
    result[info.type].subGroups.push({
      prefix,
      label: info.label,
      description: info.description || '',
      accounts: accs.sort((a, b) => (a.code || '').localeCompare(b.code || '')),
    });
    result[info.type].totalAccounts += accs.length;
  }

  for (const t of Object.values(result)) {
    t.subGroups.sort((a, b) => a.prefix.localeCompare(b.prefix));
  }
  return result;
}
